/**
 * English dictionary — source of truth for /home strings.
 *
 * Adding a key here without adding it to MessageKey in types.ts will
 * be a typecheck error. Adding a key to MessageKey without adding it
 * to every dictionary file also fails typecheck. This is the
 * tradeoff for a typed-records i18n layer with zero external deps.
 */
import type { Messages } from '../types.ts';

const en: Messages = {
  'planet.company.none': 'No matching companies',
  'planet.company.unavailable': 'Company search is temporarily unavailable.',
  'planet.company.title': 'Company search',
  'planet.company.name': 'Local company name',
  'planet.company.submit': 'Search',
  'planet.company.unknown': 'Source update time unavailable',
  'planet.company.loading': 'Searching…',
  'planet.company.retrieved': 'Retrieved',
  'planet.company.external': 'Open company search on dseek',

  // Hero band
  'hero.title': 'jubuddy',
  'hero.tagline.prefix':
    'One little buddy who shows up in your favourite anime style. Render across 40+ style families. Today, Jub is ',
  'hero.tagline.suffix': '.',
  'hero.cta.openApp': 'Open the app →',
  'hero.cta.browseStyles': 'Browse 40 styles ↓',
  'hero.cta.walkPlanet': 'Walk the planet →',
  'hero.cta.driveHongKong': 'Drive Hong Kong →',
  'hero.planetAsk.title': 'How should we open the planet?',
  'hero.planetAsk.multi': 'Select any combination',
  'hero.planetAsk.gps': 'Use GPS — nearest HD planet',
  'hero.planetAsk.time': 'Local sun — day, sunrise, sunset, night',
  'hero.planetAsk.ink': 'WH ink wash — sketch and sumi',
  'hero.planetAsk.go': 'Enter',
  'hero.planetAsk.soon': 'Opening the usual planet in {s}s',
  'hero.planetAsk.ready': 'Your choices stay until you enter',
  'hero.planetAsk.locating': 'Finding the nearest planet…',
  'hero.badge.today': "Today's Jub",

  // 40-variant marquee
  'marquee.h2': '40 anime styles. One little buddy.',
  'marquee.intro':
    'Same character, every style. From soft Sakura to neon Cyberpunk to Studio Ghibli warmth.',

  // Three surfaces
  'surfaces.h2': 'Three ways to play.',
  'surfaces.make.verb': 'Make',
  'surfaces.make.title': 'Studio Themes',
  'surfaces.make.copy':
    'Browse 36 universes. Try-it lab, sample prompts, carousel mode. Make a buddy that lives in your favourite world.',
  'surfaces.make.cta': 'Open Studio →',
  'surfaces.meet.verb': 'Meet',
  'surfaces.meet.title': 'Chatlab',
  'surfaces.meet.copy':
    'Persona-grounded LLM chats. Voice + Lottie reactions for select personas. Talk to the characters you imagine.',
  'surfaces.meet.cta': 'Open Chatlab →',
  'surfaces.battle.verb': 'Battle',
  'surfaces.battle.title': 'Campaign',
  'surfaces.battle.copy':
    'Keyword-fusion towers + 12 Jubit mascot heroes. Drop into the 30-map tower-defense campaign or endless mode.',
  'surfaces.battle.cta': 'Play Campaign →',

  // Apps mini-grid
  'apps.h2': '{count} creative and game surfaces.',
  'apps.intro':
    'jubuddy is {count} apps under one roof. Pick any one to start, swap any time.',
  // Per-app taglines (ADR-0168)
  'apps.tile.slop-hongkong.tagline':
    'Drive and explore Hong Kong streets in Sunset Ink.',
  'apps.tile.create.tagline':
    'Turn a prompt into anime-style art. 40 styles, 35+ models, NSFW gate.',
  'apps.tile.imagine.tagline':
    'Pick any image model + style, free-form prompt. The picker that started it all.',
  'apps.tile.chatlab.tagline':
    'Chat with persona-grounded LLMs. Voice + Lottie reactions for select personas.',
  'apps.tile.studio.tagline':
    'Browse 36 universes. Try-it lab + sample prompts + carousel mode.',
  'apps.tile.pictorial-book.tagline':
    '66 public series, 1,069 creatures. The canonical roster for creature games.',
  'apps.tile.creature-stages.tagline':
    '10 production stage concepts with playable TD prototypes and mode briefs.',
  'apps.tile.tcg-card-lab.tagline':
    'Creature card frames, element icons, sample lanes, and card UI runtime paths.',
  'apps.tile.meshy-ref-shelf.tagline':
    'Same-origin 2D references for Meshy concepts with runtime fallback sprites.',
  'apps.tile.workshop.tagline':
    'Build prompts brick by brick from any of 16 library domains.',

  // ── Surface matrix + Brand discipline removed (ADR-0167) ──
  //   Internal docs, not marketing. See the brand-story doc.

  // Family band — core
  'family.h2': 'Born of the Jubit family.',
  'family.body.before': 'jubuddy is the creative-play sibling to ',
  'family.body.after':
    '’s creative-tools-suite. Same auth, same credits, different brand role: jubuddy is where you play; jubit.ai is where you work.',
  'family.cta': 'Visit jubit.ai →',
  // Family band — Jubit origin story (ADR-0167)
  'family.origin.h3': 'JUBIT, EXPLAINED',
  'family.origin.formula': 'Jubit = 朱比特 (Jupiter) + Judgement Bit',
  'family.origin.body':
    'We value the finest humanity and collaborate with AI minds for a better world.',
  'family.origin.orbit':
    'As Jupiter’s 95 moons orbit in harmony, we bring 95+ AI models from 12+ providers into one unified orbit.',
  'family.origin.chatlab.cta': 'Explore ChatLab →',

  // Etymology section (ADR-0167)
  'etymology.h2': 'The name behind the buddy.',
  'etymology.intro':
    'jubuddy is a tiny piece of wordplay folded into a brand. The pieces:',
  'etymology.ju.label': 'Ju',
  'etymology.ju.unit': '单位：聚',
  'etymology.ju.gloss':
    'Magic. Charm. The pull that draws a story together. The unit is 聚 — “gather”.',
  'etymology.buddy.label': 'Buddy',
  'etymology.buddy.gloss':
    'Companionship. The friend who shows up. 陪伴 — to keep someone company.',
  'etymology.combined.label': 'Jubuddy = 聚一 (Jub It)',
  'etymology.combined.gloss':
    'The little spell that gathers your gang into one. Same buddy, every style.',
  'etymology.tagline': 'Make. Meet. Battle.',

  // Showcase section (ADR-0167)
  'showcase.h2': 'See what jubuddy creates.',
  'showcase.intro':
    'Every tile below was generated inside /jub. Tap any to open the same prompt and remix it.',
  'showcase.cta': 'Open Jub Create →',

  // Footer
  'footer.line1': 'jubuddy · part of the Jubit AI family',
  'footer.line2': '40 styles · 12 heroes · 30 maps',
  'footer.privacy': 'Privacy',
  'footer.terms': 'Terms',

  // Language switcher
  'lang.switcher.label': 'Language',

  // ── gghere.com homepage (src/worlds/) ─────────────────────────────
  // Copy is byte-identical to the previously hard-coded JSX. Counts
  // ({cityCount}, {planetTotal}, …) interpolate from WORLD_CARDS /
  // groundTruth.ts at the call site — never typed here.
  // Header nav
  'gghere.nav.aria': 'Primary',
  'gghere.nav.cities': 'Cities',
  'gghere.nav.data': 'Data',
  'gghere.nav.honestly': 'Honestly',
  'gghere.nav.play': 'Play →',
  // Launch Bay hero
  'gghere.hero.kicker': 'Tiny planets, built from real Earth',
  'gghere.hero.headline.line1': 'We turned {cityCount} real cities',
  'gghere.hero.headline.line2': 'into planets you can walk around.',
  'gghere.hero.subhead':
    '{planetTotal} of them, across {continents} continents, built from four and a half million real building footprints. Every street traced from open map data, then bent until the horizon curves under your feet — and then we gave you a tram, a rocket and a delivery job.',
  'gghere.hero.cta.walk': 'Walk {place} →',
  'gghere.hero.cta.drop': 'Drop me somewhere',
  'gghere.hero.micro': 'It runs in a browser tab. There is no account to make.',
  // The Hong-Kong-only assistant band — {scope} is ASSISTANT_SCOPE
  'gghere.hk.h2': 'In {scope}, the planet knows what is happening on it.',
  'gghere.hk.body':
    'Drop a pin anywhere on the Hong Kong planets and it reads that exact spot back to you from live government open data — the next departures from the nearest station, car-park vacancy, temperature, air quality, rainfall and any active warning. Then you can keep asking about it, in English, 繁體中文, 한국어, 日本語 or ไทย.',
  'gghere.hk.scopeNote':
    "{scope} only, for now — it is wired to Hong Kong's open-data feeds. The other {otherCount} cities are built from the same open map data, but have no live layer yet.",
  'gghere.hk.cta': 'Try it on {place} →',
  // City roster (the picker)
  'gghere.roster.aria': 'Pick a city',
  'gghere.roster.label': 'Where to?',
  'gghere.roster.placeholder': 'Type a city — Tokyo, Vienna, Cape Town',
  'gghere.roster.filter.aria': 'Filter by continent',
  'gghere.roster.filter.all': 'All',
  'gghere.roster.count.one': '{count} city',
  'gghere.roster.count.other': '{count} cities',
  'gghere.roster.empty': 'No city matches “{query}”.',
  'gghere.roster.empty.clear': 'Clear',
  // Number wall — "Counted, not claimed."
  'gghere.numbers.h2': 'Counted, not claimed.',
  'gghere.numbers.lead':
    "Untagged figures are read live out of the app's own roster, so they can't disagree with it. Tagged ones were counted from the files that ship with the site, on the date shown.",
  'gghere.numbers.tile.cities': 'cities',
  'gghere.numbers.tile.planets': 'planets',
  'gghere.numbers.tile.continents': 'continents',
  'gghere.numbers.measuredTag': 'measured {date}',
  // Scope pill shared by NumberWall + HomeFooter
  'gghere.pill.hkOnly': 'Hong Kong only',
  // Honest FAQ
  'gghere.faq.h2': 'The honest bit',
  'gghere.faq.real-data.q': 'Is this real geographic data, or a stylised guess?',
  'gghere.faq.real-data.a': 'Yes — real buildings and streets from OpenStreetMap, real elevation from Copernicus DEM. Bent into planets.',
  'gghere.faq.guide-scope.q': 'Is the local guide in every city?',
  'gghere.faq.guide-scope.a': '{scope} only, for now.',
  'gghere.faq.signage.q': 'Why don’t the shops have their real names?',
  'gghere.faq.signage.a': 'On purpose. Real places, fictional storefronts.',
  // Footer — closer + crawlable index
  'gghere.footer.closer.h2':
    'Somewhere on this list is a street you know — and you have never once seen it from the outside.',
  'gghere.footer.closer.pickCity': 'Pick a city ↑',
  'gghere.footer.closer.browseAll': 'Browse every planet →',
  'gghere.footer.roster.h3': 'Every city',
  'gghere.footer.roster.counts':
    '{cityCount} cities · {planetTotal} planets · {continentCount} continents',
  'gghere.footer.roster.planetsUnit': 'planets',
  // Licence-required attribution — translators must keep source names,
  // licence names (ODbL, Copernicus DEM, CSDI, DATA.GOV.HK) and the meaning intact.
  'gghere.footer.attribution': 'Map data © OpenStreetMap contributors (ODbL) · Copernicus DEM · Hong Kong open data (CSDI, DATA.GOV.HK). No real signage.',
  'gghere.footer.family': 'gghere is part of the Jubit AI universe.',
  'gghere.footer.newTab': '(opens in a new tab)',
  'gghere.carousel.aria': 'City planets',
  'gghere.carousel.flyIn': 'Fly into {place}',
  'gghere.carousel.cardAlt': '{place} as a cube planet — captured from the live app',
  'gghere.carousel.planets': '{count} planets',
  'gghere.carousel.enter': 'Enter',
  'gghere.carousel.prev': 'Previous city',
  'gghere.carousel.next': 'Next city',
  'gghere.carousel.position': '{place} — {index} of {total}',
  'gghere.carousel.cursor.drag': 'Drag',
  // Planet HUD chrome — compact labels; place names stay English.
  'planet.hud.weather': 'Weather',
  'planet.live.quiet': 'Quiet',
  'planet.live.ambient': 'Ambient',
  'planet.live.live': 'Live',
  'planet.live.aria': 'Live data: {level}. Tap to change.',
  'planet.card.more': '+{n} more nearby',
  'planet.hud.weather.day': 'Day',
  'planet.hud.weather.golden': 'Golden hour',
  'planet.hud.weather.night': 'Night',
  'planet.hud.weather.rain': 'Rain',
  'planet.hud.weather.typhoon': 'Typhoon',
  'planet.hud.weather.undersea': 'Undersea',
  'planet.hud.column': 'Space rises · release sinks',
  'planet.hud.stars': 'Stargaze',
  'planet.hud.stars.over': 'Sky over {place}',
  'planet.hud.stars.lines': 'Figures',
  'planet.hud.stars.names': 'Names',
  'planet.hud.stars.exit': 'Leave the sky',
  'planet.hud.stars.hint': 'Drag to look around · scroll to zoom · Esc to leave',
  'planet.scan.landmark': 'Landmark',
  'planet.scan.landmark.height': 'Height {m} m',
  'planet.scan.landmark.storeys': '≈ {n} storeys',
  'planet.scan.landmark.footprint': 'Footprint {m} m',
  'planet.scan.landmark.crowns': 'Crowns {place}',
  'planet.scan.landmark.stamp': 'Stamp {n} / {total}',
  'planet.scan.landmark.stampNew': 'New stamp',
  'planet.scan.landmark.inside': 'inside',
  'planet.room.catalog': 'Look rooms',
  'planet.room.catalog.hint': 'Choose a landmark, then walk up to enter its room.',
  'planet.room.catalog.empty': 'There are no landmark rooms on this planet yet.',
  'planet.room.catalog.show': 'Show landmark: {name}',
  'planet.room.catalog.visited': 'Visited inside',
  'planet.room.catalog.rain': 'Rain in this scene. Choose a landmark. Walk to its door. Enter when the room is ready.',
  'planet.room.catalog.storm': 'Storm in this scene. Choose a landmark. Walk to its door. Enter when the room is ready.',
  'planet.room.catalog.nearby': 'Nearby room',
  'planet.room.enter': 'Enter the room',
  'planet.room.walkUp': 'Walk up to enter',
  'planet.room.leave': 'Leave',
  'planet.room.credit': 'AI-painted scene',
  'planet.room.stamp': 'Stamp pressed · inside',
  'planet.room.closed': 'This room is closed for now',
  'planet.room.opening': 'Opening the room…',
  'planet.room.opening.named': 'Crossing into the {floor}…',
  'planet.room.opening.dream': 'Crossing into the still water…',
  'planet.room.look': 'Drag or use the arrow keys to look around',
  'planet.room.view.controls': "Room view controls",
  'planet.room.view.window': "Window view",
  'planet.room.view.interior': "Look inside",
  'planet.room.view.zoomOut': "Zoom out",
  'planet.room.view.zoomIn': "Zoom in",
  'planet.room.view.city': "City window",
  'planet.room.view.city.hint': "Use the city view captured on entry. Turn off to restore the painted window.",
  'planet.room.photo': 'Photo',
  'planet.room.photo.aria': 'Save a photo from inside this room',
  'planet.room.photo.saved': 'Saved',
  'planet.room.photo.retry': 'Retry',
  'planet.room.frame.empty': 'Empty picture frame',
  'planet.room.frame.hang': 'Hang a picture',
  'planet.room.frame.clear': 'Clear',
  'planet.room.frame.close': 'Close',
  'planet.room.frame.paint': 'Paper pictures',
  'planet.room.frame.guests': 'Combat girls',
  'planet.room.frame.custom': 'Custom picture',
  'planet.room.frame.pick': 'Choose a picture',
  'planet.room.frame.hint.custom': 'This frame only · no logos or signage · stays on this device',
  'planet.room.frame.failed': 'Could not hang that picture',
  'planet.room.frame.keep': 'Keep on your account',
  'planet.room.frame.hint': 'Turn to a wall to hang a picture — frames stay off the windows',
  'planet.room.frame.with': 'Picture of {name}',
  'planet.room.floors': 'Floors of this building',
  'planet.room.floor.lobby': 'Lobby',
  'planet.room.floor.mid': 'Mid-level',
  'planet.room.floor.sky': 'Sky hall',
  'planet.room.floor.deck': 'Observation deck',
  'planet.room.floor.hall': 'Hall',
  'planet.room.floor.dream': 'Inside',
  'planet.room.paint': 'Paint',
  'planet.room.paint.aria': "Change this room's look. Walls only — windows keep the landmark's view.",
  'planet.room.paint.sheet': 'Room look',
  'planet.room.paint.interior': 'Interior wash',
  'planet.room.paint.windows': 'Window hour',
  'planet.room.paint.closed': 'Custom rooms (50 ju) — the painter is not open yet. Nothing is charged. The wash stays on the walls; the windows keep this landmark’s view.',
  'planet.room.paint.price': '{n} ju',
  'planet.room.paint.hint': 'Wash the walls — windows keep the planet outside',
  'planet.room.paint.official': 'Official',
  'planet.room.paint.followHud': 'Follow HUD',
  'planet.room.paint.live': 'Live planet',
  'planet.room.paint.live.aria': "Show this landmark's live planet landscape in the windows",
  'planet.room.paint.signin': 'Sign in to paint this room',
  'planet.room.paint.insufficient': 'Not enough ju to paint this room',
  'planet.room.paint.working': 'Painting your room… about a minute',
  'planet.room.paint.done': 'Your room is ready',
  'planet.room.paint.enterMine': 'Enter your room',
  'planet.room.paint.mine': 'Mine',
  'planet.room.paint.community': 'Community ({n})',
  'planet.room.paint.by': 'by {name}',
  'planet.room.paint.publish': 'Publish this room',
  'planet.room.paint.busy': 'The painter is busy — try again',
  'planet.room.paint.failed': 'The painter could not finish — nothing was charged',
  'planet.room.paint.have': 'You have {n} ju',
  'planet.room.upload': 'Upload pano',
  'planet.room.upload.closed':
    'Upload a 2:1 room pano (20 ju) — hosting is not open yet. Nothing is charged. Paint (50 ju) is a separate depth pass and is also closed.',
  'planet.room.upload.signin': 'Sign in to upload a room pano',
  'planet.room.upload.insufficient': 'Not enough ju to host this pano',
  'planet.room.upload.working': 'Hosting your pano…',
  'planet.room.upload.done': 'Your pano is on the walls',
  'planet.room.upload.badRatio': 'That file is not a 2:1 room pano. Nothing was charged.',
  'planet.room.beat.windows': 'The city leans in through the glass.',
  'planet.room.paint.lost': 'We lost the connection. Tap again to check — you won\'t be charged twice.',
  'planet.room.paint.noKit': 'This room can\'t be painted yet — upload your own pano instead.',
  'planet.room.paint.tier': 'Painting is open to {tier} players.',
  'planet.room.paint.slot': 'Your published room here is on another floor — this would replace it.',
  'planet.room.upload.failed': 'Hosting could not finish — nothing was charged.',
  'planet.room.paint.elsewhere': 'Your earlier room is still being made — {n}%',
  'planet.room.beat.windows.dream': 'The paper sky is still holding water.',
  'planet.room.beat.windows.harbour': 'The harbour holds the light.',
  'planet.room.beat.windows.canal': "Water and brick, a canal's width away.",
  'planet.room.beat.windows.rooftops': 'Rooftops, tiled and close.',
  'planet.room.beat.windows.iron': 'Iron lace, and the river city far below.',
  'planet.room.beat.windows.needle': 'The city is a map from here.',
  'planet.room.beat.windows.desert': 'Sand-coloured glass and a long horizon.',
  'planet.room.beat.windows.mountain': 'A mountain leans across the water.',
  'planet.room.beat.windows.garden': 'Gardens and tiled roofs, close enough to name.',
  'planet.room.beat.windows.street': 'The street is a colour through the glass.',
  'planet.room.beat.wall': 'Turned from the view — a wall that will take a picture.',
  'planet.room.beat.sky': 'Beams, and the idea of a ceiling.',
  'planet.room.beat.floor': 'The floor is a colour, not a drop.',
  'planet.scan.landmark.sky': 'Sky above it',
  'planet.scan.landmark.photo': 'Photo',
  'planet.scan.building': 'Building',
  'planet.scan.inspect': 'Inspect',
  'planet.scan.building.footprint': 'Footprint {w} × {d} m',
  'planet.scan.building.height': 'Height {m} m',
  'planet.scan.building.floors': '≈ {n} floors',
  'planet.scan.building.noHeight': 'Height not surveyed here',
  'planet.scan.lot.fallen': 'Empty lot',
  'planet.scan.lot.stood': 'Stood {m} m',
  'planet.scan.lot.rebuilt': 'Rebuilt by you',
  'planet.scan.lot.rebuild': 'Rebuild · {price} 🪙',
  'planet.scan.lot.sheet': 'Raise a tower on the lot',
  'planet.scan.lot.tiers': 'Tiers',
  'planet.scan.lot.crown': 'Crown',
  'planet.scan.lot.crown.flat': 'Flat',
  'planet.scan.lot.crown.spire': 'Spire',
  'planet.scan.lot.crown.dome': 'Dome',
  'planet.scan.lot.crown.ring': 'Ring',
  'planet.scan.lot.height': 'Height {pct}%',
  'planet.scan.lot.tone': 'Tone',
  'planet.scan.lot.confirm': 'Rebuild for {price} 🪙',
  'planet.scan.lot.short': 'Need {n} more 🪙',
  'planet.scan.lot.close': 'Close',
  'planet.scan.lot.publishSignin': 'Sign in to publish',
  'planet.scan.skin': 'Re-skin',
  'planet.scan.skin.sheet': 'Your kit on the tower',
  'planet.scan.skin.pick': 'Choose a picture',
  'planet.scan.skin.hint': 'A square tile works best · no logos or signage · shown to others only when you publish',
  'planet.scan.skin.apply': 'Apply skin',
  'planet.scan.skin.remove': 'Remove skin',
  'planet.scan.skin.signin': 'Sign in to keep a skin',
  'planet.scan.skin.failed': 'Could not save the skin',
  'planet.scan.skin.saved': 'Skin applied',
  'planet.scan.skin.mode.poster': 'Poster — the whole tower',
  'planet.scan.skin.mode.tile': 'Tiles',
  'planet.scan.skin.scale.1': 'Small tiles',
  'planet.scan.skin.scale.5': 'Big tiles · 5×',
  'planet.scan.skin.hint.poster': 'Any picture up to 1 MB · covers every face of the tower, cropped to fill · no logos or signage',
  'planet.scan.building.replace': 'Real building',
  'planet.scan.building.sheet': 'A real building on this tower',
  'planet.scan.building.pick': 'Choose a photo of a building',
  'planet.scan.building.hint': 'One clear photo of the whole building · forged into 3D in 5–15 minutes · private until you publish · Pro',
  'planet.scan.building.working': 'Forging… {pct}%',
  'planet.scan.building.done': 'Your building is up',
  'planet.scan.building.failed': 'The forge could not make a building from this photo',
  'planet.scan.building.tier': 'Forging a building needs Pro',
  'planet.scan.building.busy': 'The forge is busy — try again in a little while',
  'planet.scan.building.restore': 'Restore the curated tower',
  'planet.scan.building.signin': 'Sign in to forge a building',
  'planet.scan.building.survey': 'Use the surveyed building',
  'planet.scan.building.surveyHint': 'The Lands Department’s own 3D model of this tower · no photo · private until you publish · Pro',
  'planet.scan.building.surveySignin': 'Sign in to make it real',
  'planet.scan.building.surveying': 'Fetching the surveyed model…',
  'planet.scan.building.surveyUnavailable': 'No surveyed model for this tower yet',
  'planet.scan.building.source': 'Source: {attribution}',
  'planet.scan.building.registerHeight': 'Register {m} m',
  'planet.scan.building.registerStoreys': '{n} storeys',
  'planet.scan.building.thumbPlaceholder': 'Surveyed model',
  'planet.scan.plot.title': 'An empty lot',
  'planet.scan.plot.hint': 'The bake left this ground bare. The survey says what really stands here — pay, and it is built on the spot; re-skin or publish it after. Pro',
  'planet.scan.plot.build': 'Build the real building here',
  'planet.scan.plot.working': 'Surveying this lot…',
  'planet.scan.plot.done': 'The real building stands here — tap it to re-skin or publish',
  'planet.scan.plot.empty': 'Nothing stands here in the survey — the lot stays open',
  'planet.scan.plot.probe.loading': 'Checking the survey…',
  'planet.scan.plot.probe.found': '🏢 {name} · {height} m · {storeys} floors',
  'planet.scan.plot.probe.unnamed': '🏢 A building · {height} m',
  'planet.scan.plot.buildNamed': 'Build {name} here',
  'planet.scan.plot.ju': 'Not enough ju — {price} ju for {window}',
  'planet.scan.plot.ding': '丁屋 land',
  'planet.scan.plot.dingHint': 'New Territories village land — Yuen Long, Tai Po, Fanling, Sai Kung, Tuen Mun. A 丁屋 inside the 300-foot Village Environ costs ju. Pro',
  'planet.scan.plot.dingBuild': 'Build a 丁屋 here',
  'planet.scan.plot.dingWorking': 'Raising the 丁屋…',
  'planet.scan.plot.dingDone': 'The 丁屋 stands here — tap it to re-skin or publish',
  'planet.scan.plot.home': 'Private house',
  'planet.scan.plot.homeHint': 'Empty land on this planet. A house or a villa stands here for ju. It stays private until you publish.',
  'planet.scan.plot.house': 'Build a house',
  'planet.scan.plot.villa': 'Build a villa',
  'planet.scan.plot.homeWorking': 'Raising the house…',
  'planet.scan.plot.homeDone': 'It stands here. Open it to furnish a floor.',
  'planet.scan.building.leaseFor': 'Make it real for',
  'planet.scan.building.leaseUntil': 'Real until {date}',
  'planet.scan.building.reskin': 'Re-skin this building',
  'planet.scan.building.appearance': 'How the surveyed building looks',
  'planet.scan.building.facade': 'Real facade (textures)',
  'planet.scan.building.facadeNote': 'Lands Department texture maps, reduced to 512 px so the tower can wear them.',
  'planet.scan.building.mass': 'Plain mass (no textures)',
  'planet.scan.building.indoor': 'Hong Kong publishes about 600 3D indoor maps, plus walking paths in about 98 MTR stations. LOOK rooms stay the walk-in on this planet; those official floors are not a second interior.',
  'planet.scan.building.download': 'Download this building · {ju} ju',
  'planet.scan.building.downloadPay': 'Pay {usd} for the file',
  'planet.scan.building.downloadHint': 'One GLB of the building already on this lot. Ju or a card — nothing is mailed. The licence line stays with the file.',
  'planet.scan.building.renew': 'Extend the lease',
  'planet.scan.building.ju': 'Not enough ju — {price} ju for {window}',
  'planet.scan.creation.sheet': 'Publish to everyone',
  'planet.scan.creation.hint': 'An admin looks before anyone else sees it',
  'planet.scan.creation.next': 'Only you can see it. Publish it and everyone on this planet will, once an admin approves · from {price} ju',
  'planet.scan.creation.skin': 'Your skin here',
  'planet.scan.creation.rebuild': 'Your rebuilt tower here',
  'planet.scan.creation.building': 'Your building here',
  'planet.scan.creation.room': 'Your LOOK room here',
  'planet.scan.creation.placename': 'Your name for this building',
  'planet.scan.creation.lookroom': 'Your look-room here',
  'planet.scan.creation.highlight': 'Public colour highlight',
  'planet.scan.unnamed.title': 'Name this building',
  'planet.scan.unnamed.hint': 'This block is not a landmark and not in the building register. Suggest a place name, or host a private look-room. Both stay private until you publish and an admin approves.',
  'planet.scan.unnamed.nameEn': 'English name',
  'planet.scan.unnamed.nameZh': 'Chinese name',
  'planet.scan.unnamed.saveName': 'Save name · {price} ju',
  'planet.scan.unnamed.saveNameFree': 'Update name',
  'planet.scan.unnamed.look': 'Host a look-room · {price} ju',
  'planet.scan.unnamed.lookFree': 'Replace look-room',
  'planet.scan.unnamed.lookHint': 'A photo of the place. We build the 2:1 room from it. Private until you publish.',
  'planet.scan.unnamed.lookFile': 'Photo',
  'planet.scan.unnamed.publish': 'Publish for review',
  'planet.scan.unnamed.signin': 'Sign in to name this building',
  'planet.scan.unnamed.saved': 'Saved for you only. Publish it when you want an admin to let everyone see it.',
  'planet.scan.unnamed.insufficient': 'Not enough ju — needs {price} ju',
  'planet.scan.unnamed.brand': 'That name includes a transit brand. Use the place name only.',
  'planet.scan.unnamed.badName': 'Add an English or Chinese place name.',
  'planet.scan.unnamed.notEquirect': 'The look-room needs a 2:1 panorama.',
  'planet.scan.unnamed.tooLarge': 'That image is too large. Use a smaller one.',
  'planet.scan.unnamed.failed': 'Could not save that. Try again.',
  'planet.scan.arrange.title': 'Arrange this room',
  'planet.scan.arrange.hint': 'Wood, stone, paint and cloth stay on the surfaces they belong to. The picture still costs ju. Moving a sample does not.',
  'planet.scan.arrange.floor': 'Floor',
  'planet.scan.arrange.wall': 'Wall',
  'planet.scan.arrange.slat': 'Slats',
  'planet.scan.arrange.fabric': 'Upholstery',
  'planet.scan.arrange.sun': 'Sun',
  'planet.scan.arrange.save': 'Save this arrangement',
  'planet.scan.arrange.feel': 'The room reads {feel}',
  'planet.scan.arrange.wood': 'Wood',
  'planet.scan.arrange.stone': 'Stone',
  'planet.scan.arrange.paint': 'Paint',
  'planet.scan.arrange.cloth': 'Cloth',
  'planet.scan.arrange.bright': 'bright',
  'planet.scan.arrange.cozy': 'cozy',
  'planet.scan.arrange.moody': 'moody',
  'planet.scan.arrange.level': 'Level {n}',
  'planet.scan.arrange.levelsHint': 'This public 丁屋 keeps a look-room on each level. The first picture on a level is {price} ju.',
  'planet.scan.furnish.title': 'Furniture',
  'planet.scan.furnish.hint': 'A 丁屋 floor holds six pieces. The room is already paid, so moving them stays free.',
  'planet.scan.furnish.cap': '{count} / {max}',
  'planet.scan.furnish.full': 'This floor is full. Take a piece off before adding another.',
  'planet.scan.furnish.empty': 'Empty',
  'planet.scan.furnish.bed': 'Bed',
  'planet.scan.furnish.table': 'Table',
  'planet.scan.furnish.stool': 'Stool',
  'planet.scan.furnish.chest': 'Chest',
  'planet.scan.furnish.lamp': 'Lamp',
  'planet.scan.creation.private': 'Private',
  'planet.scan.creation.pending': 'Awaiting review',
  'planet.scan.creation.public': 'Public',
  'planet.scan.creation.rejected': 'Not approved',
  'planet.scan.creation.publish': 'Publish',
  'planet.scan.creation.withdraw': 'Withdraw',
  'planet.scan.creation.delete': 'Delete',
  'planet.scan.creation.none': 'Nothing of yours here yet',
  'planet.scan.creation.signin': 'Sign in to keep what you make',
  'planet.scan.creation.window.title': 'How long should everyone see it?',
  'planet.scan.creation.window.1h': '1 hour',
  'planet.scan.creation.window.1d': '1 day',
  'planet.scan.creation.window.1w': '1 week',
  'planet.scan.creation.window.1m': '1 month',
  'planet.scan.creation.window.1y': '1 year',
  'planet.scan.creation.window.price': '{ju} ju',
  'planet.scan.creation.window.free': 'Private stays free',
  'planet.scan.creation.window.tier': 'Longer windows need Pro or Max',
  'planet.scan.creation.window.forfeit': 'The clock starts when an admin approves. A rejection or a new picture forfeits it — no refunds.',
  'planet.scan.creation.window.balance': 'You have {ju} ju',
  'planet.scan.creation.window.insufficient': 'Not enough ju',
  'planet.scan.creation.window.until': 'Public until {when}',
  'planet.scan.creation.window.paid': 'Paid · publishes free',
  'planet.scan.creation.window.slot': 'Someone else holds this tower for now',
  'planet.scan.creation.window.pendingAfter': 'Awaiting review · public for {window} once approved',
  'planet.scan.creation.window.expired': 'Window ended',
  'planet.scan.creation.window.unpaid': 'Finish paying',
  'planet.scan.creation.window.failed': 'Could not publish. Try again — a retry never charges twice.',
  'planet.scan.creation.window.caps': 'You already hold as much as one player can — withdraw something first',
  'planet.scan.ghost.skin': 'Skin by @{name}',
  'planet.scan.ghost.rebuilt': 'Rebuilt by @{name}',
  'planet.scan.ghost.building': 'Building by @{name}',
  'planet.hud.allCity': 'All {place}',
  'planet.hud.veil.planet': '3D Planet',
  'planet.hud.veil.flat': 'Flat Map · leaving for dseek.ai',
  'planet.hud.loading.daily': 'Loading today\'s challenge…',
  'planet.hud.loading': 'Loading…',
  'planet.hud.loading.district': 'Loading {place} district…',
  'planet.hud.floor': 'Floor {current}/{total}',
  'planet.hud.standing': 'Ask to be located',
  'planet.hud.locate': 'Locate',
  'planet.hud.homeStamp.aria': 'Distance home: {distance}',
  'planet.julai.sit': 'Sit',
  'planet.julai.stand': 'Stand',
  'planet.julai.isle': 'To the isle',
  'planet.julai.home': 'Back to the Grey Hour',
  'planet.julai.tend': 'Tend',
  'planet.julai.sow': 'Sow',
  'planet.julai.harvest': 'Harvest',
  'planet.julai.climb': 'Climb',
  'planet.julai.descend': 'Climb down',
  'planet.julai.down': 'Down into the dark',
  'planet.julai.dive': 'Dive',
  'planet.julai.surface': 'Surface',
  'planet.julai.go': 'Take the train',
  'planet.julai.post.claim': 'Claim the bottle',
  'planet.julai.post.drop': 'Drop a bottle',
  'planet.julai.post.open': 'Pull the cork',
  'planet.hud.shore': 'Shore finds {found} of {total}',
  'planet.hud.skywalk': 'Skywalk stamps {found} of {total}',
  'planet.hud.skywalk.on': 'Skywalk stamps {found} of {total} · on the deck',
  'planet.hud.play.courier': 'Play Courier Rush',
  'planet.hud.play.rally': 'Play Drive',
  'planet.hud.play.kart': 'Play Drive',
  'planet.hud.play.drive': 'Play Drive',
  'planet.hud.play.park': 'Play Car Park',
  'planet.hud.play.boat': 'Play Sea Circus',
  'planet.hud.play.sail': 'Set sail on the Ether Sea',
  'planet.hud.kart': 'Drive',
  'planet.hud.drive': 'Drive',
  'planet.hud.park': 'Park',
  'planet.hud.boat': 'Sea Circus',
  'planet.hud.sail': 'Set sail',
  'planet.hud.castOff': 'Cast off',
  'planet.hud.play.castOff': 'Cast off from the Ether Sea port',
  'planet.hud.play.siege': 'Play Ultra Siege',
  'planet.hud.freedom': 'Neighborhood',
  'planet.hud.freedom.aria': 'Fix this existing neighborhood — paint, play, dress',
  'planet.hud.sculpt': 'Sculpt',
  'planet.hud.sculpt.aria': 'Raise or lower the ground under your feet',
  'planet.hud.play.ground': 'Play this facility',
  'planet.hud.play.carnival': 'Carnival of games',
  'planet.hud.play.allsports': 'All-Sports',
  'planet.freedom.title': 'Neighborhood',
  'planet.freedom.close': 'Close',
  'planet.freedom.empty': 'Walk onto a pitch, court, track or booth to play',
  'planet.freedom.underfoot': 'Paint the ground under your feet',
  'planet.freedom.near': 'On the lot',
  'planet.freedom.play': 'Play',
  'planet.freedom.dress': 'Dress',
  'planet.freedom.paint': 'Civic ground',
  'planet.freedom.block.open': 'Open block',
  'planet.freedom.block.pocket-park': 'Pocket park',
  'planet.freedom.block.plaza': 'Gathering plaza',
  'planet.freedom.block.high-street': 'High street',
  'planet.freedom.block.beach': 'Beach band',
  'planet.freedom.block.waterfront': 'Waterfront',
  'planet.freedom.opinion.open': 'A quiet lot. Neighbors have not decided what this bench is for.',
  'planet.freedom.opinion.pocket-park': 'People linger on the green. The path is for walking.',
  'planet.freedom.opinion.plaza': 'A gathering square. Someone will have opinions about this paving.',
  'planet.freedom.opinion.high-street': 'A painted street. Drive still follows the baked roads.',
  'planet.freedom.opinion.beach': 'Sand underfoot. A beach on a cell you own — the river stays put.',
  'planet.freedom.opinion.waterfront': 'Water edge on a cell you own. Bake water stays canonical.',
  'planet.freedom.civic.vegetation': 'Park',
  'planet.freedom.civic.plaza': 'Plaza',
  'planet.freedom.civic.road': 'Street',
  'planet.freedom.civic.sand': 'Beach',
  'planet.freedom.civic.water': 'Water',
  'planet.freedom.kit': 'Kit',
  'planet.freedom.weather': 'Weather',
  'planet.freedom.kick': 'Kick',
  'planet.freedom.trial': 'Trial',
  'planet.freedom.score': 'Score',
  'planet.freedom.beat': 'Beat {n}',
  'planet.freedom.finish': 'Finish',
  'planet.freedom.bond': 'Pet fight',
  'planet.freedom.bondVersus': 'vs',
  'planet.freedom.bondWaiting': 'A pet is waiting',
  'planet.freedom.bondGym': 'Gym',
  'planet.freedom.bondWin': 'You win',
  'planet.freedom.bondLose': 'You lose',
  'planet.freedom.bondDraw': 'Draw',
  'planet.freedom.bondPick': 'Pick a charm',
  'planet.freedom.bondShare': 'Share link',
  'planet.freedom.bondRoom': 'Room',
  'planet.freedom.bondHall': 'Waiting hall',
  'planet.freedom.bondWatch': 'Watch',
  'planet.freedom.bondBadge': 'Badge',
  'planet.freedom.bondSuper': 'Super',
  'planet.freedom.bondResisted': 'Resisted',
  'planet.freedom.bondReady': 'This match',
  'planet.freedom.bondStart': 'Start the match',
  'planet.freedom.bondStrike': 'Strike',
  'planet.freedom.bondAuto': 'AUTO',
  'planet.freedom.bondCast': 'Cast',
  'planet.freedom.jevAttack': 'Attack',
  'planet.freedom.jevRetreat': 'Fall back',
  'planet.freedom.jevCover': 'Cover',
  'planet.freedom.jevHold': 'Hold',
  'planet.freedom.jevHeal': 'Recover',
  'planet.freedom.jevExplore': 'Circle',
  'planet.freedom.el.fire': 'Fire',
  'planet.freedom.el.water': 'Water',
  'planet.freedom.el.wood': 'Wood',
  'planet.freedom.el.metal': 'Metal',
  'planet.freedom.el.earth': 'Earth',
  'planet.freedom.el.wind': 'Wind',
  'planet.freedom.el.spirit': 'Spirit',
  'planet.sculpt.title': 'Sculpt ground',
  'planet.sculpt.close': 'Close',
  'planet.sculpt.blurb': 'A local bump on this planet. The bake stays the map.',
  'planet.sculpt.raise': 'Raise',
  'planet.sculpt.lower': 'Lower',
  'planet.sculpt.flatten': 'Flatten',
  'planet.sculpt.underfoot': 'The cell under your feet',
  'planet.sculpt.delta': '{m} m',
  'planet.carnival.title': 'Carnival of games',
  'planet.carnival.blurb': 'Each booth is a different game on this world',
  'planet.carnival.play': 'Play',
  'planet.allsports.title': 'All-Sports',
  'planet.allsports.blurb': 'Olympic parks as one toy world — pick a park to walk it',
  'planet.allsports.visit': 'Visit',
  'planet.hud.coast': 'Coast',
  'planet.hud.coast.aria': 'Open beach and coast overlay',
  'planet.coast.title': 'Coast / Beach',
  'planet.coast.close': 'Close',
  'planet.coast.beach': 'Beach',
  'planet.coast.spline': 'Coast',
  'planet.coast.clear': 'Clear overlay',
  'planet.coast.empty': 'Bake glow lines stay the coast until you paint a beach or spline',
  'planet.coast.needCell': 'Stand on a cell you own to add a beach',
  'planet.coast.sport.honesty': 'Adds a beach on a cell you own. Does not move the Lea.',
  'planet.coast.games.honesty': 'Dune-rim beach is a local overlay on a computed planet.',
  'planet.carnival.talk': 'Talk',
  'planet.carnival.attendant': 'Attendant',
  'planet.carnival.opponent': 'Opponent',
  'planet.hud.play': 'Play',
  'planet.hud.rally': 'Drive',
  'planet.hud.siege': 'Siege',
  'planet.hud.view': 'View mode',
  'planet.hud.immersive': 'Immersive',
  'planet.hud.immersive.aria': 'Immersive view — walk the surface',
  'planet.hud.overview': 'Overview',
  'planet.hud.overview.aria': 'Overview — the diorama camera',
  'planet.hud.more': 'More controls',
  'planet.hud.more.hide': 'Hide the rest of the controls',
  'planet.hud.actions': 'Actions',
  'planet.hud.actions.hide': 'Hide actions',
  'planet.hud.stats': 'Play stats',
  'planet.hud.stats.on': 'Anonymous play stats: on — tap to turn off',
  'planet.hud.stats.off': 'Anonymous play stats: off — tap to turn on',
  'planet.hud.stats.gpc': 'Play stats off — your browser sent Global Privacy Control',
  'planet.hud.cloud.synced': 'Progress synced to your Jubit account',
  'planet.hud.cloud.syncedShort': 'Synced',
  'planet.hud.cloud.signIn': 'Sign in to keep your planet progress on every device',
  'planet.hud.cloud.signInShort': 'Sign in to sync',
  'planet.hud.browse': 'Browse every planet, in every city',
  'planet.hud.choose': 'Choose a planet — currently {planet}, {place}',
  'planet.map.cube': '{place} — whole-city cube',
  'planet.map.here': '{place} — {map}',
  'planet.map.all': 'Every city',
  'planet.map.switch': 'Switch map — {label}',
  'planet.picker.roster': '{place} planets',
  'planet.hud.pin': 'Drop a pin: tap 📌 then tap, or hold still on a lot',
  'planet.hud.pinHold': 'hold still on a lot to pin it — no 📌 needed',
  'planet.hud.drop': 'Drop me somewhere',
  'planet.hud.drop.aria': 'Drop me on a random HD planet',
  'planet.hud.home': 'Home',
  'planet.hud.home.aria': 'Home planet',
  'planet.hud.scan': 'tap the planet to scan a spot',
  'planet.hud.scanBadge': 'Spots scanned in this district — the first scan of a new spot pays +{reward} 🪙.',
  'planet.hud.nav': 'Planet navigation',
  'planet.hud.districts': 'Districts',
  'planet.hud.move': 'Move controls',
  'planet.hud.accelerate': 'Accelerate',
  'planet.hud.forward': 'Move forward',
  'planet.hud.left': 'Turn left',
  'planet.hud.right': 'Turn right',
  'planet.hud.back': 'Move back',
  'planet.hud.reverse': 'Reverse',
  'planet.hud.jump': 'Jump',
  'planet.hud.hop': 'Hop',
  'planet.hud.gas': 'Gas',
  'planet.hud.brake': 'Brake',
  'planet.hud.drift': 'Drift',
  'planet.hud.drift.aria': 'Hold to drift, release for a turbo',
  'planet.hud.turbo': 'Turbo',
  'planet.hud.turbo.aria': 'Spend the stored turbo or a banked N₂O now',
  'planet.hud.camera': 'Camera',
  'planet.hud.shop': 'Shop',
  'planet.hud.house': 'Build a New house',
  'planet.hud.house.hint': 'Pays ju and raises a 丁屋, a house, or a villa in this city, near this district.',
  'planet.hud.house.dive': 'Opens the nearest district. You pay there, and it stands in that city.',
  'planet.hud.house.seeking': 'Looking near this district…',
  'planet.hud.house.none': 'No open lot for that near this district.',
  'planet.hud.house.done': 'It stands near this district.',
  'planet.hud.photo': 'Photo',
  'planet.hud.photo.aria': 'Save a photo of this view',
  'planet.hud.photo.saved': 'Saved',
  'planet.hud.photo.retry': 'Retry',
  'planet.hud.photo.busy': 'Taking photo…',
  'planet.hud.photo.lapse': 'Timelapse',
  'planet.hud.lineup': 'Line up',
  'planet.hud.lineup.aria': 'Line up a real photo with this landscape',
  'planet.hud.lineup.title': 'Your photo',
  'planet.hud.lineup.reading': 'Opening the photo…',
  'planet.hud.lineup.placing': 'This photo has a spot. Finding it on the planet…',
  'planet.hud.lineup.noGps': 'No GPS in this photo. Nothing moved.',
  'planet.hud.lineup.incomplete': 'This file has latitude and longitude without N/S or E/W, so nothing moved.',
  'planet.hud.lineup.fix.file': 'From this file',
  'planet.hud.lineup.fix.noCompass': 'No compass in this file.',
  'planet.hud.lineup.fix.compassTrue': 'Compass {deg}° true north, from the file.',
  'planet.hud.lineup.fix.compassMag': 'Compass {deg}° magnetic, from the file. Facing was not changed.',
  'planet.hud.lineup.fix.compassUnset': 'Compass {deg}° in the file, with no true or magnetic mark. Facing was not changed.',
  'planet.hud.lineup.gpsOff': 'GPS is off, so this photo cannot be matched with where you are.',
  'planet.hud.lineup.gpsUnavailable': 'GPS did not answer, so this photo cannot be matched with where you are.',
  'planet.hud.lineup.split': 'GPS and this photo are not the same place.',
  'planet.hud.lineup.splitPlaces': "You're in {you}. This photo is {photo}. They are not the same place.",
  'planet.hud.lineup.match.gps': 'GPS',
  'planet.hud.lineup.match.planet': 'Planet',
  'planet.hud.lineup.match.photo': 'Photo',
  'planet.hud.lineup.match.gpsOff': 'Off',
  'planet.hud.lineup.match.gpsUnavailable': 'No answer',
  'planet.hud.lineup.match.gpsWait': 'Asking…',
  'planet.hud.lineup.match.noPlanet': 'This view has no position.',
  'planet.hud.lineup.match.offBelt': 'Not on a mapped belt',
  'planet.hud.lineup.unreadable': "This file's location can't be read. A JPEG that kept its GPS can.",
  'planet.hud.lineup.away': 'Taken at {place}.',
  'planet.hud.lineup.awayHere': 'Taken at {place}. This street is still {here}.',
  'planet.hud.lineup.go': 'Stand there',
  'planet.hud.lineup.pair': 'Pair',
  'planet.hud.lineup.sync': 'Sync',
  'planet.hud.lineup.synced': 'Walking on your GPS.',
  'planet.hud.lineup.syncMiss': "This planet can't stand on your GPS.",
  'planet.hud.lineup.travelling': "On the way to {place}. You'll stand there if the ground allows it.",
  'planet.hud.lineup.stoodTrue': "Standing where the photo was taken, turned to its true-north compass. The file doesn't say how tight the GPS is.",
  'planet.hud.lineup.stoodTrueAcc': 'Standing where the photo was taken (±{m} m), turned to its true-north compass.',
  'planet.hud.lineup.stoodMag': "Standing on the photo's GPS. Its compass is magnetic, so the facing was not changed.",
  'planet.hud.lineup.stoodUnspecified': "Standing on the photo's GPS. The compass angle has no true or magnetic mark, so the facing was not changed.",
  'planet.hud.lineup.stoodNone': 'Standing where the photo was taken. It has no compass, so the facing stayed as it was.',
  'planet.hud.lineup.stoodLoose': 'Near where the photo was taken (about ±{m} m). The match is approximate.',
  'planet.hud.lineup.stoodUnapplied': "Standing where the photo was taken. This view couldn't turn to the compass, so the facing is approximate.",
  'planet.hud.lineup.refused': "The photo has a spot, and this planet can't stand on it.",
  'planet.hud.lineup.close': 'Close',
  'planet.hud.lineup.ask': 'A photo of where you are. It stays on this phone, and the browser asks first.',
  'planet.hud.lineup.allow': 'Choose',
  'planet.hud.lineup.camera': 'Camera',
  'planet.hud.lineup.camera.aria': 'Take a photo with this phone and line it up',
  'planet.hud.lineup.take': 'Take',
  'planet.hud.lineup.notNow': 'Not now',
  'planet.hud.lineup.place': 'Or paste a map link',
  'planet.hud.lineup.placePh': '35.6595, 139.7005 or a map link',
  'planet.hud.lineup.placeGo': 'Stand',
  'planet.hud.lineup.placeBlank': 'That text has no latitude and longitude. Nothing moved.',
  'planet.hud.lineup.eye': 'Eye level',
  'planet.hud.lineup.eyeRefused': "This view can't drop to eye level right now.",
  'planet.hud.lineup.massAhead': 'Mapped building ahead: {w} × {d} m. Height is not in the map.',
  'planet.hud.lineup.massAheadH': 'Mapped building ahead: {w} × {d} m, {h} m tall.',
  'planet.hud.lineup.massNear': 'Nearest mapped building: {w} × {d} m. Height is not in the map, and it may not be what this view faces.',
  'planet.hud.lineup.massNearH': 'Nearest mapped building: {w} × {d} m, {h} m tall. It may not be what this view faces.',
  'planet.hud.lineup.massNone': 'No mapped building within a short walk.',
  'planet.hud.lineup.massName': 'A map name near it: {name}.',
  'planet.hud.lineup.stoodLink': 'Standing on the link. It had no facing, so the view was not turned.',
  'planet.hud.lineup.stoodLinkHead': "Standing on the link, turned to its heading ({deg}°). The link does not say how tight that point is.",
  'planet.hud.lineup.stoodLinkMiss': 'Standing on the link. It has a heading, but this view could not turn, so the facing is approximate.',
  'planet.hud.lineup.linkAway': 'The link points at {place}.',
  'planet.hud.lineup.linkLabel': 'The link calls it {name}.',
  'planet.hud.lineup.linkRefused': "The link has a spot, and this planet can't stand on it.",
  'planet.hud.lineup.shoot': 'Shoot this view',
  'planet.hud.lineup.shotSaved': 'Saved. The spot stays, so you can shoot it again.',
  'planet.hud.lineup.shotRetry': 'The shot did not save. The spot is still here.',
  'planet.hud.lineup.frameStreet': 'Street',
  'planet.hud.lineup.frameBack': 'Further',
  'planet.hud.lineup.frameNote': 'Street or further is only the camera. The place does not move.',
  'planet.hud.lineup.copy': 'Copy this spot',
  'planet.hud.lineup.copied': 'Spot link copied.',
  'planet.hud.lineup.copyFailed': 'This browser did not copy the link.',
  'planet.hud.portrait': 'Portrait',
  'planet.hud.portrait.aria': 'Frontal portrait of your character and pet',
  'planet.hud.pet': 'Companions',
  'planet.hud.giant': 'Giant',
  'planet.hud.pedestrian': 'Pedestrian',
  'planet.hud.mini': 'Mini',
  'planet.hud.scale.aria': 'Body scale {current}. Press P for {next}',
  'planet.hud.surface': 'Surface',
  'planet.hud.interior': 'Interior',
  'planet.hud.seamScreens': 'Screens',
  'planet.hud.seamScreens.aria': 'Open this seam screen',
  'planet.hud.seam.hint': 'Your picture on this wall. Private until you publish — an admin looks, then it plays here.',
  'planet.hud.seam.book': 'Advertise · GIF/MP4/WebM',
  'planet.hud.seam.pick': 'Upload up to 4 pictures, or 1 GIF/MP4/WebM',
  'planet.hud.seam.play': 'Play on this wall',
  'planet.hud.seam.reading': 'Reading the clip…',
  'planet.hud.seam.visit': 'On this wall for this visit. Sign in on the live site to keep it.',
  'planet.hud.seam.priceAtPublish': 'Public length and ju are chosen when you publish:',
  'planet.hud.seamMedia.hint': 'Up to 4 images, or 1 GIF/MP4/WebM',
  'planet.hud.seamMedia.count': 'Up to 4 files',
  'planet.hud.seamMedia.type': 'PNG, JPEG, WebP, GIF, MP4, or WebM',
  'planet.hud.seamMedia.codec': 'MP4 must be H.264. WebM must play in this browser. This file will not.',
  'planet.hud.seamMedia.size': 'Images 6 MB, GIF/MP4/WebM 12 MB',
  'planet.hud.seamMedia.motion': 'Only one GIF, MP4, or WebM',
  'planet.hud.seamMedia.long': 'Longer than this booking',
  'planet.hud.seamMedia.picked': '{n} files selected',
  'planet.hud.interiorSea': 'Sea battle',
  'planet.hud.interiorSea.aria': 'Sail the inner sea under the crust and fight the pirate raid',
  'planet.sea.fire.glyph': '炮',
  'planet.sea.fire': 'Fire',
  'planet.sea.yard.glyph': '船坞',
  'planet.sea.yard': 'Yard',
  'planet.sea.again.glyph': '再航',
  'planet.sea.again': 'Sail again',
  'planet.sea.weapon.lance.glyph': '直刺',
  'planet.sea.weapon.lance': 'Lance',
  'planet.sea.weapon.ribbon.glyph': '弧缎',
  'planet.sea.weapon.ribbon': 'Ribbon',
  'planet.sea.weapon.mortar.glyph': '抛射',
  'planet.sea.weapon.mortar': 'Mortar',
  'planet.sea.weapon.depth.glyph': '爆雷',
  'planet.sea.weapon.depth': 'Burst',
  'planet.sea.beat.skiff': 'Skiffs',
  'planet.sea.beat.cutter': 'Cutters',
  'planet.sea.beat.hulk': 'Hulks',
  'planet.sea.beat.boss': 'Boss',
  'planet.sea.wave': 'Wave {n}',
  'planet.sea.afloat': '{n} afloat',
  'planet.sea.incoming': 'incoming',
  'planet.sea.next': 'next: {beat}',
  'planet.sea.crew.gunner': 'Gunner',
  'planet.sea.crew.bosun': 'Bosun',
  'planet.sea.crew.carpenter': 'Carpenter',
  'planet.sea.crew.raise': 'Raise {role} 🪙{n}',
  'planet.sea.crew.full': 'Full crew',
  'planet.sea.crew.choose': 'Crew training',
  'planet.sea.crew.open': 'Crew',
  'planet.sea.crew.close': 'Close crew training',
  'planet.sea.crew.balance': 'Purse 🪙{n}',
  'planet.sea.crew.current': 'Current: {effect}',
  'planet.sea.crew.next': 'Next: {effect}',
  'planet.sea.crew.train': 'Train {role} · 🪙{n}',
  'planet.sea.crew.max': 'Maximum rank',
  'planet.sea.crew.reload': 'Reload',
  'planet.sea.plate.0': 'Red Shell',
  'planet.sea.plate.1': 'Paper Armour',
  'planet.sea.plate.2': 'Grey Sail',
  'planet.sea.plate.3': 'Vermilion Keep',
  'planet.sea.note.need': 'Need 🪙{n}',
  'planet.sea.note.kept': 'Kept: {weapon}',
  'planet.sea.note.sunk': 'Sunk +{n}',
  'planet.sea.note.hit': 'Hit!',
  'planet.sea.note.boss': 'Mainmast down · 🪙{n}',
  'planet.sea.note.settle': 'Wrecked · sunk {sunk} · wave {wave} · 🪙{coins}',
  'planet.sea.note.gunner': '{role} Lv{n} · damage +{p}%',
  'planet.sea.note.bosun': '{role} Lv{n} · reload −{p}%',
  'planet.sea.note.carpenter': '{role} Lv{n} · hull +{hull}',
  'planet.sea.note.plate': '{plate} · hull +{timber} · speed +{speed}% · shot +{shot}%',
  'planet.sea.fire.aria': 'Fire (J). Hold to keep firing.',
  'planet.sea.weapon.aria': '{weapon}; next: {next}',
  'planet.sea.keep.aria': 'Keep {weapon} for {n} coins',
  'planet.sea.boost.aria': 'Nitro (Shift)',
  'planet.sea.hull.aria': '{n} of {max} hull left',
  'planet.sea.crew.aria': 'Raise the next crew rank',
  'planet.sea.yard.aria': 'Open the yard',
  'planet.sea.again.aria': 'Sail again',
  'planet.sea.keys': 'WASD sail · J fire (hold) · Shift nitro · Space escape · F web · O overview',
  'planet.sea.tab.weapon.glyph': '武器',
  'planet.sea.tab.weapon': 'Armaments',
  'planet.sea.tab.hull.glyph': '船体',
  'planet.sea.tab.hull': 'Hull',
  'planet.sea.tab.girl.glyph': '舰长',
  'planet.sea.tab.girl': 'Captain',
  'planet.sea.tab.pet.glyph': '宠物',
  'planet.sea.tab.pet': 'Pet',
  'planet.sea.tab.trade.glyph': '贸易',
  'planet.sea.tab.trade': 'Trade',
  'planet.sea.tab.ride.glyph': '载具',
  'planet.sea.tab.ride': 'Ride',
  'planet.sea.yard.centre.glyph': '英雄中心',
  'planet.sea.yard.centre': 'Combat Centre',
  'planet.sea.yard.ether.glyph': '出海',
  'planet.sea.yard.ether': 'Ether Sea',
  'planet.sea.yard.back.glyph': '返回',
  'planet.sea.yard.back': 'Back',
  'planet.sea.yard.see': 'See {tab}',
  'planet.sea.yard.close.aria': 'Close the yard',
  'planet.sea.result.close.aria': 'Close the result card',
  'planet.sea.tutorial.close.aria': 'Close the controls card',
  'planet.sea.panel.collapse.aria': 'Collapse the sea panel',
  'planet.sea.panel.expand.aria': 'Expand the sea panel',
  'planet.sea.yard.centre.aria': 'Open the Combat Centre on {name}',
  'planet.sea.yard.ether.aria': 'Cast off into the Ether Sea',
  'planet.sea.yard.port': 'To port',
  'planet.sea.yard.roster': 'The street roster',
  'planet.sea.yard.drag': 'Drag to look around',
  'planet.sea.yard.noPet': 'No pet',
  'planet.sea.yard.none': 'None',
  'planet.sea.yard.offChart': 'This world is not on the ether chart.',
  'planet.sea.yard.tradeBlurb': 'Chart and packet on this sheet. Surface pays harbour duty; inner-sea win sells duty-free.',
  'planet.sea.yard.petBlurb': 'The chosen pet stands to port of the sailor.',
  'planet.sea.yard.rideBlurb': 'The same street roster, from the ship.',
  'planet.sea.yard.rideEmpty': 'No street vehicles yet — open the surface roster.',
  'planet.sea.yard.crewBlurb': 'The sailor on deck is whoever you brought. The captain is this ship’s post.',
  'planet.sea.yard.doorNote': 'Crew trains · Weapon keep/cycle here · Centre hires.',
  'planet.sea.yard.crew.glyph': '船员',
  'planet.sea.yard.crew': 'Crew',
  'planet.sea.yard.weapon.glyph': '火器',
  'planet.sea.yard.weapon': 'Weapon',
  'planet.sea.yard.crew.aria': 'Open crew training',
  'planet.sea.yard.weapon.aria': 'Weapon — cycle or keep in Yard',
  'planet.sea.yard.weaponPrice': 'Equipped · {name}',
  'planet.sea.yard.weaponTip': 'On lance — Cycle, then Keep · 🪙{n}',
  'planet.sea.yard.weaponTipKept': 'Already kept — Cycle for another',
  'planet.sea.yard.weaponKeep': 'Keep · 🪙{n}',
  'planet.sea.yard.weaponCycle': 'Cycle',
  'planet.sea.yard.weaponShopTitle': 'Weapon shop · Embed',
  'planet.sea.yard.weaponKeptBadge': 'Kept',
  'planet.sea.yard.weaponEmbedHint': 'Tap a module or Cycle — Keep embeds · 🪙{n}',
  'planet.sea.yard.weaponLevelBadge': 'L{n}',
  'planet.sea.yard.weaponUpgrade': 'Upgrade L{level} · 🪙{n}',
  'planet.sea.yard.weaponLevelMax': 'Max L2',
  'planet.sea.yard.berthStatus': 'Berth upkeep · 🪙{n}/day — due today',
  'planet.sea.yard.berthStatusPaid': 'Berth upkeep · 🪙{n}/day — paid today',
  'planet.sea.yard.powderStatus': 'Sortie powder · 🪙{n}',
  'planet.sea.yard.powderStatusFree': 'Sortie powder · first free today, then 🪙{n}',
  'planet.sea.yard.captainHire': 'Hire at Combat Centre · 🪙{n}',
  'planet.sea.yard.captainOwned': 'Owned — open Combat Centre',
  'planet.sea.yard.goldShopTitle': 'Harbour gold shop · 🥇{n}',
  'planet.sea.yard.goldSinkBuy': '{label} · 🥇{n}',
  'planet.sea.yard.goldSinkOwned': '{label} · owned',
  'planet.sea.yard.petCosmeticBuy': '{label} · {n} ju',
  'planet.sea.yard.petCosmeticOwned': '{label} · owned',
  'planet.sea.yard.rideSkinBuy': '{label} · 🪙{n}',
  'planet.sea.yard.rideSkinOwned': '{label} · owned',
  'planet.sea.yard.rideSpecialForm': 'Roadster special form · ju lease',
  'planet.sea.yard.autopilotStrip': 'Autopilot Soft Fare · {n} ju (Ether helm)',
  'planet.sea.yard.autopilotCta': 'Hire autopilot · {n} ju',
  'planet.sea.yard.combatAutoFireStrip': 'Combat auto-fire · {n} ju / life (not voyage autopilot)',
  'planet.sea.yard.combatAutoFireCta': 'Enable auto-fire · {n} ju',
  'planet.sea.yard.combatAutoFireOn': 'Combat auto-fire ON · this life',
  'planet.sea.autoFire.enable': 'Auto · {n} ju',
  'planet.sea.autoFire.enable.aria': 'Enable combat auto-fire for {n} ju this life',
  'planet.sea.autoFire.on': 'Auto ON',
  'planet.sea.yard.seasonShopTitle': 'Season harbour cosmetics',
  'planet.sea.yard.seasonCosmeticBuy': '{label} · 🪙{n}',
  'planet.sea.yard.seasonCosmeticOwned': '{label} · owned this season',
  'planet.sea.yard.rig.glyph': '装备',
  'planet.sea.yard.rig': 'Equipped',
  'planet.sea.yard.owned.glyph': '已拥有',
  'planet.sea.yard.owned': 'Owned',
  'planet.sea.yard.upgrade': '升级 · Upgrade 🪙{n}',
  'planet.sea.yard.coinHonesty': 'Hull upgrades cost planet coins — never ju for hull power.',
  'planet.sea.yard.lock': '🔒',
  'planet.sea.stat.mass': 'Displacement',
  'planet.sea.stat.speed': 'Speed',
  'planet.sea.stat.shot': 'Shot',
  'planet.sea.stat.hull': 'Hull',
  'planet.sea.dock': 'Dock',
  'planet.sea.banner.wave': 'Wave {n} · {beat}',
  'planet.sea.banner.boss': 'The pirate boss!',
  'planet.sea.banner.bossDown': 'Mainmast down!',
  'planet.sea.boss.aria': 'Boss timber {p}%',
  'planet.sea.result.title': 'Wrecked',
  'planet.sea.result.sunk': 'Sunk',
  'planet.sea.result.wave': 'Wave',
  'planet.sea.result.purse': 'Purse',
  'planet.sea.result.best': 'Best',
  'planet.sea.result.tradeOff': 'Trade off',
  'planet.sea.result.newBest': 'New best!',
  'planet.sea.tutorial.title': 'Sea battle',
  'planet.sea.tutorial.goal': 'Recover floating cargo, defeat four sector flagships, and grow your warship. Choose blueprints as you level up; spend salvage at the field drydock. Q / E / R / X activate your ship systems.',
  'planet.sea.tutorial.sail': 'Sail',
  'planet.sea.tutorial.fire': 'Fire; auto fire targets nearby pirates',
  'planet.sea.tutorial.nitro': 'Nitro: one charge, then it refills',
  'planet.sea.tutorial.hop': 'Hop',
  'planet.sea.tutorial.touch': 'Steer with the left pad; hold Fire to shoot. Tap a ship system above for a special attack or repair. The bolt spends nitro.',
  'planet.sea.tutorial.ok': 'Set sail',
  'planet.sea.skill.aria': 'Companion skills',
  'planet.sea.skill.cast.aria': 'Cast {skill}',
  'planet.sea.skill.deckVolley': 'Deck Volley',
  'planet.sea.skill.deckBurst': 'Deck Burst',
  'planet.sea.skill.deckMend': 'Deck Mend',
  'planet.hud.shell.cavity': 'Air cavity',
  'planet.hud.shell.undersea': 'Undersea',
  'planet.hud.shell.interior': 'Interior',
  'planet.hud.shell.core': 'Core',
  'planet.hud.shell.loco.sail': 'Sail, stand, hop',
  'planet.hud.shell.loco.swim': 'Swim, buoyant',
  'planet.hud.shell.loco.stand': 'Stand, hop, up to the core',
  'planet.hud.shell.loco.float': 'Drift, up to the core',
  'planet.hud.shell.loco.look': 'Look at the core',
  'planet.hud.shell.dive': 'Dive',
  'planet.hud.shell.surface': 'Surface',
  'planet.hud.walk': 'Walk',
  'planet.hud.ride': 'Ride',
  'planet.hud.swap': 'Swap',
  'planet.hud.switchVehicle': 'Switch vehicle',
  'planet.hud.hopOff': 'Hop off',
  'planet.hud.rideVessel': 'Ride {vessel}',
  'planet.hud.vessel.train': 'train',
  'planet.hud.vessel.ferry': 'ferry',
  'planet.hud.vessel.ship': 'ship',
  'planet.hud.travel': 'Travel to {name}',
  'planet.gate.title': 'Travel',
  'planet.gate.body': 'This gate connects to the neighbouring planets:',
  'planet.gate.close': 'Close',
  'planet.hud.enter': 'Enter {name}',
  'planet.hud.dive': 'Dive into {district}',
  'planet.fare.toilet.urgent': 'A toilet. In {zone}. Quickly.',
  'planet.fare.toilet.polite': 'Could you find me a toilet in {zone}?',
  'planet.fare.tap.thirsty': "I'm parched — a water tap in {zone}.",
  'planet.fare.tap.bottle': "My bottle's empty. Tap in {zone}?",
  'planet.fare.bay.late': "I'm late. Any metered bay in {zone}.",
  'planet.fare.bay.circling': 'Been circling for twenty minutes. A bay in {zone}.',
  // Pin + chat chrome — verbs and aria; place names stay data.
  'planet.ask.pinned': 'Pinned spot',
  'planet.ask.closePin': 'Close the pinned spot card',
  'planet.ask.scanning': 'Scanning this spot…',
  'planet.ask.empty': 'Nothing came back for this spot just now.',
  'planet.ask.told': 'What this spot can say',
  'planet.ask.carParks': 'Car parks',
  'planet.ask.nearby': 'Nearby',
  'planet.ask.askSpot': 'Ask about this spot',
  'planet.ask.readSpot': 'Read this spot',
  'planet.ask.isoMap': 'Iso Map',
  'planet.ask.daily': 'Daily',
  'planet.ask.news': 'News',
  'planet.ask.terminal': 'Terminal',
  'planet.ask.dseekGroup': 'Scan and ask on dseek',
  'planet.ask.daily.aria': 'Open the daily map for this spot in a new window',
  'planet.ask.news.aria': 'Open news in a new window',
  'planet.ask.terminal.aria': 'Open the data terminal in a new window',
  'planet.ask.title': 'Ask the planet',
  'planet.ask.closeChat': 'Close the chat',
  'planet.ask.emptyHint': 'Ask about what is around you — trains, weather, what to see.',
  'planet.ask.placeholder': 'Ask about this place…',
  'planet.news.near': 'Near here',
  'planet.news.story': 'Story on the flat map',
  'planet.news.story.aria': 'Open this landmark’s story on the flat map in a new window',
  'planet.news.door.aria': 'Open {label} on dseek Life in a new window. For fun only.',
  'planet.service.funNote': 'for fun',
  'planet.news.group': 'Headlines near this spot',
  'planet.family.venueDoor': 'Venues, parking & reminders',
  "planet.parking.priorities": "Top 3 near this pin",
  "planet.parking.rule": "Meters within 300 m. Car parks within 800 m. Distances are straight-line.",
  "planet.parking.free": "Free spaces: {n}",
  "planet.parking.meter": "Meter {n}",
  "planet.parking.carpark": "Car park",
  "planet.parking.fromPin": "{m} m from pin",
  "planet.parking.watchArea": "Watch this area",
  "planet.parking.stopArea": "Stop area watch",
  "planet.parking.watching": "Watching parking near this pin",
  "planet.parking.changed": "Parking changed. Check the top 3.",
  "planet.parking.noSpaces": "No fresh spaces found. Check the alternatives.",
  "planet.parking.targetLost": "Your target has no confirmed free space. Choose another target.",
  "planet.parking.stale": "Live data is unavailable. Do not use the previous counts.",
  "planet.parking.alternatives": "Other car parks",
  "planet.parking.directions": "Open directions",
  "planet.parking.useLocation": "Use my location",
  "planet.parking.stopLocation": "Stop location",
  "planet.parking.locationPending": "Waiting for an accurate location.",
  "planet.parking.locationUnavailable": "Location is unavailable. You can open directions.",
  "planet.parking.locationNote": "Directions use your location when it is available. Alerts pause when this page is hidden.",
  "planet.parking.near": "You are near {place}. Open directions.",
  "planet.parking.closer": "{place} is closer to you. Compare directions.",
  "planet.parking.selected": "Selected target: {place}",
  "planet.parking.liveRequired": "Set Live data to Live to watch this area.",
  "planet.parking.unknown": "Availability unknown.",
  'planet.kerb.observed': '{free} of {total} observed bays free',
  'planet.family.checked': 'Checked {time} HKT',
  'planet.family.refresh': 'Refresh',
  'planet.family.refreshing': 'Refreshing…',
  'planet.kerb.group': 'On-street parking near here',
  'planet.kerb.bays': '{free} of {total} metered bays free',
  'planet.kerb.rate': 'HK${rate}/hr',
  'planet.kerb.mood.open': 'Many observed bays are free.',
  'planet.kerb.mood.easy': 'Some observed bays are free.',
  'planet.kerb.mood.tight': 'Few observed bays are free.',
  'planet.kerb.mood.hopeless': 'Very few bays are free. Check car parks.',
  'planet.kerb.mood.unknown': 'no live reading',
  'planet.kerb.parks': 'Car parks with spaces: {n}',
  'planet.kerb.nearby': 'Within 300 m · observed street bays',
  'planet.kerb.arrivalCaveat': 'Spaces may change before you arrive.',
  'planet.kerb.refresh': 'Refresh',
  'planet.kerb.refreshing': 'Refreshing…',
  'planet.kerb.watch': 'Watch meter {n}',
  'planet.kerb.unwatch': 'Stop watching meter {n}',
  'planet.kerb.watchLimit': 'Up to 6 meters this session',
  'planet.kerb.watchNote': 'Selected private-car meters only. Bubbles appear here while this district is open in Live mode.',
  'planet.kerb.watchOpened': 'A private-car space opened at meter {n}',
  'planet.kerb.watchFull': 'Meter {n} now has no observed free private-car spaces',
  'planet.kerb.watchDismiss': 'Dismiss',
  'planet.kerb.watchInspect': 'View meter {n}',
  'planet.kerb.watchGroup': 'Parking watches',
  'planet.kerb.watchStopAll': 'Stop all watches',
  'planet.flatmap.group': 'Illustrated flat map',
  'planet.flatmap.title': 'Flat map · {place}',
  'planet.flatmap.close': 'Close the flat map',
  'planet.flatmap.canvas': 'Illustrated map of this district',
  'planet.flatmap.loading': 'Loading the illustration…',
  'planet.flatmap.none': 'No illustration for this place',
  'planet.flatmap.none.body': 'The flat map has no drawing of this district yet. The full map still opens on dseek.',
  'planet.flatmap.offframe': 'You have walked off this illustration',
  'planet.flatmap.dseek': 'Open the full map on dseek',
  'planet.flatmap.credit': 'Illustration © dseek.ai, baked from Hong Kong Lands Department 3D data via CSDI / DATA.GOV.HK.',
  'planet.venue.group': 'Public venue nearby',
  'planet.venue.hours': 'Open {hours}',
  'planet.venue.credit': 'Venue details © Leisure and Cultural Services Department, HKSAR, via data.gov.hk.',
  'planet.cityWeather.title': 'Tokyo regional forecast',
  'planet.cityWeather.loading': 'Loading regional forecast…',
  'planet.cityWeather.unavailable': 'Regional forecast unavailable.',
  'planet.cityWeather.scope': 'Tokyo prefecture, including islands · Japanese original',
  'planet.cityWeather.issued': 'Issued {time} JST',
  'planet.cityWeather.source': 'JMA · original bulletin',
  'planet.cityWeather.processing': 'Source: JMA. Fields normalized; original text retained.',
  'planet.schoolNet.group': 'School net at this point',
  'planet.schoolNet.primary': 'Primary net {net}',
  'planet.schoolNet.secondary': 'Secondary code {net}',
  'planet.schoolNet.outside': 'No published primary or secondary net contains this point.',
  'planet.schoolNet.note': 'This is a boundary, not a vacancy or an admissions result.',
  'planet.schoolNet.door': 'School register',
  'planet.schoolNet.credit': 'School-net geometry © Education Bureau.',
  'planet.healthcare.group': 'Nearest healthcare facility',
  'planet.healthcare.nearest': '{name} · {m} m',
  'planet.healthcare.note': 'A facility record is not clinical advice.',
  'planet.healthcare.door': 'Clinic register',
  'planet.healthcare.credit': 'Hospitals © Hospital Authority. Clinics © Department of Health registers.',
  'planet.funAi.entertainment': 'Entertainment only. A reading here is not a fact to act on.',
  'planet.funAi.guest': 'A guest gets one reading a day.',
  'planet.amenity.group': 'Public amenities nearby',
  'planet.amenity.toilet': 'Nearest toilet · {m} m',
  'planet.amenity.tap': 'Nearest tap · {m} m',
  'planet.amenity.temp.a': 'ambient',
  'planet.amenity.temp.c': 'cold',
  'planet.amenity.temp.ac': 'ambient or cold',
  'planet.amenity.temp.hc': 'hot or cold',
  'planet.amenity.temp.hac': 'hot, ambient or cold',
  'planet.amenity.onlyHot': 'The only tap on this belt that does hot.',
  'planet.amenity.fewHot': 'Only {n} taps on this belt serve hot water.',
  'planet.spot.group': 'Landmark nearby',
  'planet.spot.chip': 'Walk it on dseek',
  'planet.spot.chip.aria': 'Open {place} on dseek’s flat map',
  'planet.spot.credit': 'Places © dseek.ai — its own catalogue of Hong Kong landmarks.',
  'planet.pitch.group': 'Today’s pitch report',
  'planet.pitch.free': '{n} free court-sessions in today’s full-day report, including earlier sessions',
  'planet.pitch.slots': 'Free today',
  'planet.pitch.weekend': 'The coming weekend reports {weekend} free court-sessions across Hong Kong, against about {weekday} per weekday.',
  'planet.pitch.report': '{n} free court-sessions reported for today',
  'planet.pitch.preview': 'Upcoming starts in the short preview: {slots}',
  'planet.pitch.previewNone': 'The short preview has no upcoming starts; check SmartPLAY for the full day.',
  'planet.pitch.totalNote': 'The daily total includes earlier sessions. The preview is partial.',
  'planet.pitch.confirm': 'Confirm availability and book on SmartPLAY.',
  'planet.pitch.book': 'Book on SmartPLAY',
  'planet.service.navigate': 'Directions',
  'planet.pitch.credit': 'Pitch availability © Leisure and Cultural Services Department, via data.gov.hk.',
  'planet.harbour.group': 'Harbour departures',
  'planet.harbour.count': '{n} ocean-going ships left Hong Kong today',
  'planet.harbour.last': 'The last was {name}, a {type}.',
  'planet.harbour.last.at': 'The last was {name}, a {type}, at {at}.',
  'planet.harbour.top': '{type} — {n} of the {total}.',
  'planet.harbour.credit': 'Vessel departures © Marine Department, HKSAR, via dseek.ai.',
  'planet.amenity.credit': 'Toilets © Food and Environmental Hygiene Department; taps © Leisure and Cultural Services Department, via data.gov.hk.',
  'planet.cinema.group': 'Cinema nearby',
  'planet.cinema.tonight': '{n} official screening(s) tonight · {time}',
  'planet.cinema.notice': 'Programme notice',
  'planet.cinema.credit': 'Film programmes © Leisure and Cultural Services Department; cinema locations © Culture, Sports and Tourism Bureau, via data.gov.hk.',
  'planet.screen.desk.title': 'Research desk',
  'planet.screen.desk.open': 'Open the desk',
  'planet.screen.desk.closed': 'The desk is closed for now.',
  'planet.screen.desk.verified': '{n} claims verified',
  'planet.screen.desk.credit': 'Curated research © dseek.ai — analyst-reviewed themes, not headlines.',
  'planet.screen.data.title': 'Data desk',
  'planet.screen.data.hk': 'Hong Kong',
  'planet.screen.data.us': 'United States',
  'planet.screen.data.open': 'Browse data',
  'planet.screen.data.close': 'Back to room',
  'planet.screen.data.reload': 'Reload',
  'planet.screen.data.external': 'Open in new tab',
  'planet.screen.data.loading': 'Loading dseek…',
  'planet.screen.data.hint': 'Use the page’s own tabs to explore. If it does not display or needs sign-in, open it in a new tab.',
  'planet.screen.data.predict': 'Predict',
  'planet.screen.predict.title': 'Prediction markets',
  'planet.screen.predict.yes': 'YES price',
  'planet.screen.predict.ready': 'Market prices · snapshot',
  'planet.screen.predict.stale': 'Older snapshot',
  'planet.screen.predict.loading': 'Loading public market data…',
  'planet.screen.predict.unavailable': 'Market data unavailable',
  'planet.screen.predict.fetched': 'Fetched {time}',
  'planet.screen.predict.source': 'Polymarket Gamma · dseek Predict',
  'planet.screen.predict.open': 'Open Predict',
  'planet.search.open': 'Search an address',
  'planet.search.aria': 'Search a Hong Kong address and drop the pin there',
  'planet.search.placeholder': 'Address, building or street…',
  'planet.search.none': 'No address found',
  'planet.search.unavailable': 'Address search is unavailable right now.',
  'planet.search.credit': 'Address Lookup Service © Digital Policy Office, via data.gov.hk.',
  'planet.search.credit.places': 'Place names © Lands Department. Street addresses © Digital Policy Office, via data.gov.hk.',
  'planet.search.credit.gsi': 'Street addresses © Geospatial Information Authority of Japan.',
  'planet.search.aria.address': 'Search a street address in this city and drop the pin there',
  'planet.search.addressOnly': 'Results stay inside this city. A title from another city stays off this planet.',
  'planet.search.pin': 'Pin',
  'planet.search.walk': 'Walk',
  'planet.search.walkNote': 'Walks across the surface to this pin.',
  'planet.search.on': 'On {place}',
  'planet.search.go': 'Go there',
  'planet.search.edge': 'Off the walkable ground.',
  'planet.pin.scan': 'Pin to scan',
  'planet.pin.ask': 'Pin to ask',
  'planet.pin.centre': 'This pin is the centre of the reading.',
  'planet.park.hub': 'Parking around this pin',
  'planet.park.free': '{n} free',
  'planet.park.metered': 'metered',
  'planet.park.near': 'Near',
  'planet.park.far': 'Far',
  'planet.walk.line': '{from} → {to} · {m} m · {min} min',
  'planet.walk.indoor': 'The official path uses an indoor link.',
  'planet.presentation.hk.landsd-location-search': 'Place name © Lands Department Location Search.',
  'planet.presentation.hk.landsd-pedestrian-route': 'Recommended walk © Lands Department. The number is the official path.',
  'planet.presentation.jp.gsi-address': 'Street address © Geospatial Information Authority of Japan.',
  'planet.brief.pill': 'Brief',
  'planet.brief.pill.aria': 'Open the district brief — headlines near this planet',
  'planet.brief.title': 'Around {place}',
  'planet.brief.empty': 'Nothing located near here right now.',
  'planet.brief.unavailable': 'Nearby news is unavailable right now.',
  'planet.brief.partial': 'Some news sources are unavailable.',
  'planet.brief.retry': 'Try again',
  'planet.brief.walk': 'Walk there',
  'planet.brief.walk.aria': 'Drop the pin at the district centre and open the spot card',
  'planet.brief.close': 'Close',
  'gghere.mode.pick': 'Pick a mode',
  'gghere.mode.explore': 'Explore',
  'gghere.mode.explore.blurb': 'Walk, drive and ride the city',
  'gghere.mode.courier': 'Courier Rush',
  'gghere.mode.courier.blurb': 'Time-attack deliveries',
  'gghere.mode.survival': 'Survival',
  'gghere.mode.survival.blurb': 'Hold the streets against waves',
  'gghere.mode.rally': 'Coin Rally',
  'gghere.mode.rally.blurb': 'Race the coin line',
  'gghere.chip.modeIn': '{mode} in {place}',
  'notfound.body': 'No buddy lives here.',
  'notfound.back': 'Back to base',
  'chrome.loading': 'Loading…',
  'chrome.loading.named': 'Loading {label}…',
  'chrome.chunkError.body': 'Part of this page failed to load — a newer version may be out.',
  'chrome.chunkError.reload': 'Reload',

  // Jub Studio standing chrome
  'jub.nav.aria': 'Jub Studio navigation',
  'jub.nav.studio': 'Studio',
  'jub.nav.library': 'Library',
  'jub.nav.more': 'More',
  'jub.nav.tapToOpen': 'Tap to open',
  'jub.nav.seeAllApps': 'See all apps',
  'jub.nav.home': 'Home',
  'jub.nav.theme.toLight': 'Switch to light mode',
  'jub.nav.theme.toDark': 'Switch to dark mode',
  'jub.rail.aria': 'Jub Studio primary navigation',
  'jub.rail.settings': 'Settings',
  'jub.tab.aria': 'Jub Studio sections',
  'jub.tab.create': 'Create',
  'jub.tab.feed': 'Feed',
  'jub.tab.chat': 'Chat',
  'jub.tab.discover': 'Discover',
  'jub.tab.profile': 'Profile',
  'jub.tab.filter': 'Filter',
  'jub.primary.feed': 'Feed',
  'jub.primary.create': 'Create',
  'jub.primary.filter': 'Filter',
  'jub.primary.chat': 'Chat',
  'jub.primary.discover': 'Discover',
  'jub.library.creations': 'My creations',
  'jub.library.liked': 'Liked',
  'jub.library.saved': 'Saved',
  'jub.more.create': 'Create',
  'jub.more.imagine': 'Imagine',
  'jub.more.chatlab': 'Chatlab',
  'jub.more.themes': 'Themes',
  'jub.more.book': 'Book',
  'jub.more.stages': 'Stages',
  'jub.more.cards': 'Cards',
  'jub.more.poker': 'Poker',
  'jub.more.meshy': 'Meshy',
  'jub.more.workshop': 'Workshop',
  'jub.more.standee': 'Standee',

};

export default en;
